import { PageHero, CTASection } from "@/components/Section";
import FramedPhoto from "@/components/FramedPhoto";
import Reveal from "@/components/Reveal";
import { Linkedin, Mail } from "lucide-react";

const FOUNDERS = [
  {
    name: "Luke Seymour",
    role: "Founder",
    photo:
      "https://customer-assets.emergentagent.com/job_ops-framework/artifacts/djar2ta3_1633572222556.jpeg",
    bio: "I've spent the last 15 years building and leading operational businesses across New Zealand.\n\nMost recently I took a service business from 6 figures in annual revenue to 8 figures — 10x'ing the headcount across four regions. Along the way I learnt what actually breaks businesses under growth — poor systems, reactive management, weak reporting and owners carrying too much themselves.\n\nBackbench exists because I've lived those problems. I've hired through labour shortages, managed cashflow under pressure, built leadership teams, restructured businesses, improved profitability and built the systems that allow businesses to grow without everything depending on the owner.\n\nI don't believe most owner-led businesses need another consultant. They need experienced operators who can bring structure, accountability and commercial discipline into the business and leave it stronger than they found it.\n\nThat's what Backbench does.",
    focus: ["10x headcount growth", "6 → 8 figure revenue", "Four regions, NZ-wide"],
  },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero
        eyebrow="About"
        title="Built by an operator. For operators."
        intro="Backbench was founded by a senior operator who scaled a NZ service business from 6 figures to 8 figures in revenue — 10x'ing the headcount across four regions. Every system, every reporting rhythm, every hard call — lived, not learned in a slide deck."
        testId="about-hero"
      />

      {/* WHY WE BUILT BACKBENCH — mission */}
      <section
        className="relative bg-[#0A192F] text-white border-b border-[#E2E8F0] overflow-hidden"
        data-testid="about-mission-section"
      >
        <div className="bb-grid-bg absolute inset-0 opacity-[0.07] pointer-events-none" />
        <div className="relative max-w-[1440px] mx-auto px-6 lg:px-12 py-20 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#F68712]">
                Why we built Backbench
              </div>
              <h2 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1.05]">
                Most businesses don't fail from lack of work.
                <br />
                <span className="text-[#F68712]">They fail from lack of structure.</span>
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-7 lg:pl-12 lg:border-l border-white/15">
            <Reveal delay={100}>
              <div className="space-y-5 text-base lg:text-lg text-white/80 leading-relaxed">
                <p>
                  We built Backbench to solve the problems we saw repeatedly inside trade
                  and service businesses — messy reporting, reactive decisions and operational chaos.
                </p>
                <p>
                  Owners doing $2M–$15M+ don't need more consultants. They need senior operators
                  who will roll up their sleeves and build the structure the business was always meant to run on.
                </p>
                <p className="text-white font-semibold text-xl lg:text-2xl pt-4 border-t border-white/10">
                  Not theory. <span className="text-[#F68712]">Structure.</span>
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section
        className="bg-white border-b border-[#E2E8F0]"
        data-testid="about-founders-section"
      >
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-20 lg:py-32">
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#F68712]">
            Founders
          </div>
          <h2 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1.05] text-[#0A192F] max-w-3xl">
            Operator-led. Structure-first.
          </h2>

          <div className="mt-16 lg:mt-20 grid grid-cols-1 border-t border-l border-[#E2E8F0] max-w-4xl">
            {FOUNDERS.map((f, i) => (
              <article
                key={f.name}
                className="bb-card border-r border-b border-[#E2E8F0] p-8 lg:p-12 flex flex-col gap-8"
                data-testid={`founder-${i}`}
              >
                <div className="grid grid-cols-[120px_1fr] sm:grid-cols-[160px_1fr] gap-6 items-start">
                  <div className="relative">
                    <img
                      src={f.photo}
                      alt={f.name}
                      className="w-full aspect-square object-cover grayscale-[0.15] border border-[#0A192F]/10"
                      data-testid={`founder-${i}-photo`}
                    />
                    <span className="absolute -top-2 -left-2 w-3 h-3 border-t-2 border-l-2 border-[#F68712]" />
                    <span className="absolute -bottom-2 -right-2 w-3 h-3 border-b-2 border-r-2 border-[#F68712]" />
                  </div>
                  <div>
                    <span className="font-mono text-[11px] tracking-widest text-[#F68712] font-bold">
                      0{i + 1} · {f.role.toUpperCase()}
                    </span>
                    <h3
                      className="mt-2 text-2xl lg:text-3xl font-extrabold tracking-tight text-[#0A192F] leading-tight"
                      data-testid={`founder-${i}-name`}
                    >
                      {f.name}
                    </h3>
                    <div className="mt-3 flex items-center gap-3 text-[#475569]">
                      <a
                        href="/contact"
                        className="inline-flex items-center gap-1.5 text-xs font-bold tracking-widest uppercase hover:text-[#F68712] transition-colors"
                        data-testid={`founder-${i}-contact-link`}
                      >
                        <Mail size={13} strokeWidth={2.25} />
                        Get in touch
                      </a>
                    </div>
                  </div>
                </div>

                <div className="space-y-4 text-base lg:text-lg text-[#0A192F] leading-relaxed font-medium">
                  {f.bio.split("\n\n").map((para, idx) => (
                    <p key={idx}>{para}</p>
                  ))}
                </div>

                <div className="mt-auto pt-6 border-t border-[#E2E8F0]">
                  <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#475569] mb-3">
                    Focus
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {f.focus.map((tag) => (
                      <span
                        key={tag}
                        className="font-mono text-[11px] tracking-wide text-[#0A192F] border border-[#0A192F]/20 px-2.5 py-1.5"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Why Backbench */}
      <section
        className="bg-[#F8FAFC] border-b border-[#E2E8F0]"
        data-testid="about-why-section"
      >
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 py-20 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-4">
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#0A192F]/60">
              Our model
            </div>
            <h2 className="mt-4 text-3xl lg:text-5xl font-extrabold tracking-tighter leading-[1.05] text-[#0A192F]">
              Your team. At fractional capacity.
            </h2>
          </div>
          <div className="lg:col-span-4 lg:pl-10 lg:border-l border-[#0A192F]/15 space-y-5 text-base lg:text-lg text-[#475569] leading-relaxed">
            <p>
              Backbench isn't a consultancy. We're a fractional team that becomes part of yours —
              giving you access to senior operators, AI specialists, software architects and
              governance talent that would normally be out of reach for a $2M–$15M+ business.
            </p>
            <p>
              You don't pay full-time salaries. You don't get slide decks. You get embedded support
              at the capacity you need — hours, days or ongoing.
            </p>
            <p className="text-[#0A192F] font-semibold">
              You keep the controls. We bring the discipline — and the team.
            </p>
          </div>
          <div className="lg:col-span-4">
            <FramedPhoto
              src="https://customer-assets.emergentagent.com/job_ops-framework/artifacts/b2ogzmwy_Messenger_creation_4CFFE853-6FBF-4279-A02F-5336857AB035.jpeg"
              alt="Backbench operator at desk planning systems"
              aspect="aspect-[4/5]"
              eyebrow="EMBEDDED · DAY ONE"
              label="STRUCTURE · SYSTEMS · SCALE"
              testId="about-our-model-photo"
            />
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
